import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { SessionHelper } from '../../helpers/SessionHelper';
import { ProfileHelper } from '../../helpers/ProfileHelper';
import { MainActions } from '../../actions/MainActions';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { ShopLocation } from '../../models/ShopLocation';
import { Header } from '../../common-components/Layout/Header/Header';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { SectionsPage } from '../../common-components/Layout/SectionsPage/SectionsPage';
import { ActionsBox } from '../../common-components/Layout/ActionsBox/ActionsBox';
import { FlexColumnListHeader } from '../../common-components/Layout/FlexColumnList/FlexColumnListHeader';
import { FlexColumnListRow } from '../../common-components/Layout/FlexColumnList/FlexColumnListRow';
import { FlexColumnListFooter } from '../../common-components/Layout/FlexColumnList/FlexColumnListFooter';
import { IFlexColumnListItem } from '../../common-components/Layout/FlexColumnList/IFlexColumnListItem';
import { IActionsBoxItem } from '../../common-components/Layout/ActionsBox/IActionsBoxItem';
import { IDropdownButtonItem } from '../../common-components/DropdownButton/DropdownButton';
import { If } from '../../common-components/If/If';
import { AppliedFiltersToListLocations } from './AppliedFiltersToListLocations/AppliedFiltersToListLocations';
import { ModalFiltersToListLocations } from './ModalFiltersToListLocations/ModalFiltersToListLocations';
import { ModalNewLocation } from './ModalNewLocation/ModalNewLocation';
import { ModalLocationDetails } from './ModalLocationDetails/ModalLocationDetails';
import { ModalLocationContact } from './ModalLocationContact/ModalLocationContact';
import { ModalEnableTransactionalCommunication } from './ModalEnableTransactionalCommunication/ModalEnableTransactionalCommunication';
import { CellDetail, ColumnHint } from './styles';
import {
  IFilterToListLocationsOutput,
  applyLocationFilter,
  defaultLocationFilter,
  formatDateTime,
  getAssemblyTypeText,
  getYesNoText
} from './locationUi';

const ActionsKey = {
  Filter: 'filter',
  ClearFilter: 'clear_filter',
  Update: 'update',
  Insert: 'insert'
};

const actions: IActionsBoxItem[] = [
  {
    key: ActionsKey.Filter,
    text: strings.Filter
  },
  {
    key: ActionsKey.ClearFilter,
    text: 'Limpar filtros',
    minWidth: '120px'
  },
  {
    key: ActionsKey.Update,
    text: 'Atualizar'
  },
  {
    key: ActionsKey.Insert,
    text: 'Inserir'
  }
];

// nome, ID Shopify e opções têm largura fixa; as demais colunas dividem o espaço restante pelo peso
// (o cabeçalho tem a coluna "_" de 2px a mais que a linha)
const fixedColumnsWidth = 220 + 120 + 65;
const flexColumnsWeight = 95 + 160 + 150 + 150 + 180 + 150 + 125;
const flexWidth = (weight: number) => ({
  width: `calc((100% - ${fixedColumnsWidth + 2}px) * ${weight / flexColumnsWeight})`,
  widthRow: `calc((100% - ${fixedColumnsWidth}px) * ${weight / flexColumnsWeight})`
});

const columns: IFlexColumnListItem[] = [
  {
    field: 'name',
    title: 'Nome',
    width: '220px'
  },
  {
    field: 'shopifyId',
    title: 'ID Shopify',
    width: '120px'
  },
  {
    field: 'omnichannelText',
    title: <ColumnHint title='Omnichannel'>Omni</ColumnHint>,
    ...flexWidth(95),
    align: 'center'
  },
  {
    field: 'manageOrderInSislogCell',
    title: <ColumnHint title='Usa o Sislog para gestão de pedidos'>Usa Sislog</ColumnHint>,
    ...flexWidth(160)
  },
  {
    field: 'assemblyTypeText',
    title: 'Tipo de montagem',
    ...flexWidth(150)
  },
  {
    field: 'storeContactInfo',
    title: 'Contato',
    ...flexWidth(150),
    maxHeight: '60px'
  },
  {
    field: 'emailAddress',
    title: 'E-mail',
    ...flexWidth(180)
  },
  {
    field: 'storeOpeningHours',
    title: <ColumnHint title='Horário de funcionamento'>Funcionam.</ColumnHint>,
    ...flexWidth(150),
    maxHeight: '60px'
  },
  {
    field: 'transactionalCommunicationSinceText',
    title: <ColumnHint title='Comunicação transacional ativa desde'>Transacional desde</ColumnHint>,
    ...flexWidth(125)
  },
  {
    field: 'genericalOptionsRow',
    title: 'Opções',
    width: '65px',
    align: 'center',
    type: 'options'
  },
  {
    field: '_',
    title: '',
    width: '2px'
  }
];

const GenericalOptionsRow = {
  Details: 'details',
  EditContact: 'edit_contact',
  EnableTransactionalCommunication: 'enable_transactional_communication'
};

interface ILocationRow extends ShopLocation {
  omnichannelText: string;
  manageOrderInSislogCell: React.ReactNode;
  assemblyTypeText: string;
  transactionalCommunicationSinceText: string;
}

const ManageLocations: React.FC = () => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const {
    getLocationListService,
    toastService
  } = React.useContext(ServicesContext);

  const [loading, setLoading] = React.useState<boolean>(true);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  const [appliedFilter, setAppliedFilter] = React.useState<IFilterToListLocationsOutput>(defaultLocationFilter);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [modalFilterVisible, setModalFilterVisible] = React.useState<boolean>(false);
  const [modalNewVisible, setModalNewVisible] = React.useState<boolean>(false);
  const [detailsLocation, setDetailsLocation] = React.useState<ShopLocation | undefined>(undefined);
  const [contactLocation, setContactLocation] = React.useState<ShopLocation | undefined>(undefined);
  const [transactionalLocation, setTransactionalLocation] = React.useState<ShopLocation | undefined>(undefined);

  const isAdmin = ProfileHelper.profilesThatCanManageLocations.some(p => p === session?.user?.profileType);

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => [
    {
      text: strings.Home,
      route: routes.Home
    }
  ], []);

  const optionGroups = React.useMemo(() => [
    {
      key: 'genericalOptionsRow',
      rowOptions: (row: ILocationRow) => {
        const options: IDropdownButtonItem[] = [
          { key: GenericalOptionsRow.Details, text: 'Detalhar' },
          { key: GenericalOptionsRow.EditContact, text: 'Editar contato e funcionamento' }
        ];
        if (!row.transactionalCommunicationEnabledSinceDate) {
          options.push({ key: GenericalOptionsRow.EnableTransactionalCommunication, text: 'Habilitar comunicação transacional' });
        }
        return options;
      },
      click: genericalOptionsRowHandle
    }
  ], []);

  const filteredRows = React.useMemo(
    () => applyLocationFilter(locations, appliedFilter).map(toRow),
    [locations, appliedFilter]);

  const amountOfPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const page = Math.min(currentPage, amountOfPages);
  const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    // a rota só existe para admin; a checagem aqui protege contra uma sessão trocada com a página aberta
    if (!isAdmin) {
      navigate(routes.Home);
      return;
    }

    load();
  }, [session, navigate]);

  return (
    <>
      <SectionsPage
        loading={loading}
        header={{
          padding: '0px 63px',
          backgroundColor: '#fff',
          items: [
            <Header
              key={'header'}
              title={strings.ManageLocations}
              breadcrumbItems={breadcrumbItems}
            />
          ]
        }}
        body={{
          padding: '0px 63px',
          backgroundColor: 'transparent',
          header: [
            <AppliedFiltersToListLocations
              key={'applied-filters'}
              appliedFilters={appliedFilter}
            />,
            <ActionsBox
              key={'actions-box'}
              defaultItemWidth='80px'
              actions={actions}
              click={actionBoxClick}
              loading={loading}
            />,
            <FlexColumnListHeader
              name='locations'
              columns={columns}
              loading={loading}
            />
          ]
        }}
        footer={{
          padding: '10px 63px',
          backgroundColor: '#fff',
          items: [
            <FlexColumnListFooter
              key={'locations_footer'}
              currentPage={page}
              currentPageSize={pageRows.length}
              amountOfPages={amountOfPages}
              totalAmount={filteredRows.length}
              pageSize={pageSize}
              changePageSize={changePageSize}
              changePage={setCurrentPage}
              loading={loading}
            />
          ]
        }}
      >
        <FlexColumnListRow
          name='locations'
          columns={columns}
          values={pageRows}
          loading={loading}
          optionGroups={optionGroups}
        />
      </SectionsPage>

      <If condition={modalFilterVisible}>
        <ModalFiltersToListLocations
          appliedFilters={appliedFilter}
          close={() => setModalFilterVisible(false)}
          finish={(values) => {
            setModalFilterVisible(false);
            setAppliedFilter(values);
            setCurrentPage(1);
          }}
        />
      </If>

      <If condition={modalNewVisible}>
        <ModalNewLocation
          close={() => setModalNewVisible(false)}
          finish={() => {
            setModalNewVisible(false);
            load();
          }}
        />
      </If>

      <If condition={!!detailsLocation}>
        {detailsLocation ? (
          <ModalLocationDetails
            location={detailsLocation}
            close={() => setDetailsLocation(undefined)}
          />
        ) : null}
      </If>

      <If condition={!!contactLocation}>
        {contactLocation ? (
          <ModalLocationContact
            location={contactLocation}
            close={() => setContactLocation(undefined)}
            finish={() => {
              setContactLocation(undefined);
              load();
            }}
          />
        ) : null}
      </If>

      <If condition={!!transactionalLocation}>
        {transactionalLocation ? (
          <ModalEnableTransactionalCommunication
            location={transactionalLocation}
            close={() => setTransactionalLocation(undefined)}
            finish={() => {
              setTransactionalLocation(undefined);
              load();
            }}
          />
        ) : null}
      </If>
    </>
  );

  function load() {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    mainActions.setNotWait(true);
    setLoading(true);

    getLocationListService.handle()
      .then(result => {
        const list = [...(result?.value?.locations || [])]
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
        setLocations(list);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoading(false);
      });
  }

  function changePageSize(size: number) {
    setPageSize(size > 0 ? size : 25);
    setCurrentPage(1);
  }

  function toRow(location: ShopLocation): ILocationRow {
    return {
      ...location,
      omnichannelText: getYesNoText(location.isOmnichannel),
      manageOrderInSislogCell: (
        <>
          {getYesNoText(location.manageOrderInSislog)}
          {location.manageOrderInSislog && location.manageOrderInSislogSinceDate ? (
            <CellDetail>desde {formatDateTime(location.manageOrderInSislogSinceDate)}</CellDetail>
          ) : null}
        </>
      ),
      assemblyTypeText: getAssemblyTypeText(location.assemblyType),
      transactionalCommunicationSinceText: formatDateTime(location.transactionalCommunicationEnabledSinceDate)
    };
  }

  function actionBoxClick(key: string) {
    if (key === ActionsKey.Filter) {
      setModalFilterVisible(true);
      return;
    }
    if (key === ActionsKey.ClearFilter) {
      setAppliedFilter(defaultLocationFilter);
      setCurrentPage(1);
      return;
    }
    if (key === ActionsKey.Update) {
      load();
      return;
    }
    if (key === ActionsKey.Insert) {
      setModalNewVisible(true);
      return;
    }
  }

  function genericalOptionsRowHandle(key: string, row: ILocationRow) {
    if (key === GenericalOptionsRow.Details) {
      setDetailsLocation(row);
      return;
    }
    if (key === GenericalOptionsRow.EditContact) {
      setContactLocation(row);
      return;
    }
    if (key === GenericalOptionsRow.EnableTransactionalCommunication) {
      setTransactionalLocation(row);
      return;
    }
  }
}

export default ManageLocations;
